// Page Gestion (lot B12) : l'arrière-boutique du tableau de service. Trois onglets — les documents du serveur
// (collèges et fiches, OCR et mise en forme), les voix des podcasts, la couverture des livres. Le périmètre du lot
// est resserré : mêmes fonctions, mêmes actions historiques, mais aux jetons, aux composants et sans gestionnaire
// inline. Les états se lisent au mot (aimants .puce), jamais à la couleur seule.
import { html, attr, brut } from '../../core/html.js';
import { state } from '../../core/store.js';
import { actions } from '../../core/actions.js';
import { ico } from '../icones.js';

var ONGLETS = [
    ['documents', 'Documents', 'file-text'],
    ['voix', 'Voix', 'microphone'],
    ['couverture', 'Couverture', 'image'],
    ['comptes', 'Comptes', 'user'],
];
var TITRES = {
    documents: ['Les documents du serveur', brut('Collèges et fiches EDN déposés dans <code>edn_data/</code>. L\'OCR en extrait le texte, y compris quand le PDF est un scan que le lecteur habituel ne sait pas lire.')],
    voix: ['Les voix des podcasts', 'Les voix proposées par Mistral, et celles que tu fabriques toi-même à partir d\'un extrait sonore de quelques secondes.'],
    comptes: ['Les comptes utilisateurs', brut('Qui s\'est inscrit, ce qu\'il a au planning, ce qu\'il travaille — et de quoi couper ou rendre un accès. <strong>Le mot de passe n\'y figure pas&nbsp;: il est haché, le clair n\'existe nulle part.</strong> Pour dépanner quelqu\'un, fabrique-lui un lien de réinitialisation.')],
    couverture: ['La couverture du livre', 'L\'image qui fait la première page quand tu extrais les QCM ou la LCA en PDF. Choisis-la ici : elle part sur le serveur, il n\'y a rien à déposer par FTP.'],
};

function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
// Chiffres à la française : séparateur de milliers, virgule décimale.
function nombre(n) { return (n || 0).toLocaleString('fr-FR'); }
function euros(n) { return (n || 0).toFixed(2).replace('.', ','); }
function occupe(cle) { return state.gestion.busy === cle; }

// --- actions nouvelles : ce que le rendu historique faisait par un gestionnaire inline a désormais un nom.
actions.gestionFermerLotFerme = function() { state.gestion.lotFerme = null; window.renderContent(); };
actions.gestionVoixActualiser = function() { window.loadPodcastVoices(true); };
// Le fichier arrive par data-change-action : l'élément est le dernier argument, l'action historique l'attend en
// premier. Ces deux relais remettent les arguments dans l'ordre sans toucher aux fonctions d'origine.
actions.gestionVoixExtrait = function(el) { actions.gestionVoixFichier(el); };
actions.gestionCouvertureChoisir = function(livre, el) { actions.gestionCouvertureFichier(el, livre); };

// --- bandeaux : erreur, message, lot fermé sur le compte, clé absente. Chacun porte son aimant.
function bandeaux(avecLots) {
    var g = state.gestion, out = [];
    if (g.erreur) out.push(html`<div class="gst-bandeau gst-bandeau--danger" role="alert">
        <span class="puce puce-danger">Erreur</span>
        <div class="gst-bandeau-corps"><p>${g.erreur}</p></div>
    </div>`);
    if (g.message) out.push(html`<div class="gst-bandeau gst-bandeau--ok" role="status">
        <span class="puce puce-ok">Fait</span>
        <div class="gst-bandeau-corps"><p>${g.message}</p></div>
    </div>`);
    if (avecLots && g.lotFerme) {
        var gf = g.lotFerme.genre;
        out.push(html`<div class="gst-bandeau gst-bandeau--warn">
            <span class="puce puce-warn">Lots fermés</span>
            <div class="gst-bandeau-corps">
                <h2 class="gst-bandeau-titre">L'API par lots n'est pas ouverte sur ce compte Mistral</h2>
                <p>Réponse de Mistral : ${g.lotFerme.message}</p>
                <p>L'OCR à la demande, lui, fonctionne : c'est la seule différence. Active la facturation sur console.mistral.ai pour obtenir les lots (moitié prix), ou lis les documents un par un dès maintenant, au plein tarif.</p>
                <div class="gst-bandeau-actions">
                    <button type="button" class="btn btn-secondaire btn-sm" data-action="gestionOcrTout" data-args="${attr([gf])}"${g.busy ? html` disabled` : ''}>Lire tout ${gf === 'fiche' ? 'les fiches' : 'les collèges'} un par un</button>
                    <button type="button" class="btn btn-fantome btn-sm" data-action="gestionFermerLotFerme">Fermer</button>
                </div>
            </div>
        </div>`);
    }
    return out;
}

// --- onglet Documents -----------------------------------------------------------------------------------------
function jauge(fait, total) {
    var p = total ? Math.min(100, Math.round(fait / total * 100)) : 0;
    return html`<span class="gst-jauge" role="img" aria-label="${p} % lu"><span style="--p:${(p / 100).toFixed(3)}"></span></span>`;
}

function carteBase() {
    var g = state.gestion, b = g.base;
    if (!b) {
        return html`<div class="gst-carte">
            <div class="gst-carte-tete">
                <h2>Base de données</h2>
                <button type="button" class="btn btn-fantome btn-sm" data-action="gestionBase" data-args="${attr([false, false])}"${g.busy ? html` disabled` : ''}${occupe('base') ? html` aria-busy="true"` : ''}>Diagnostiquer</button>
            </div>
            <p>Vérifier les colonnes et la taille du planning (« Data too long »).</p>
        </div>`;
    }
    if (b.sans_base) return '';
    // L'alerte se lit sur la ligne qui la porte (aimant « Trop petite ») et sur le bouton de réparation, jamais
    // par un filet rouge autour d'une carte qui rapporte par ailleurs que tout va bien.
    var alerte = (b.alertes || []).length > 0;
    var pl = b.planning;
    return html`<div class="gst-carte">
        <div class="gst-carte-tete">
            <h2>Base ${b.base || '?'}</h2>
            <span class="gst-carte-mention">MySQL ${b.mysql || '?'}${b.max_allowed_packet ? ' · paquet max ' + window.octets(b.max_allowed_packet) : ''}</span>
        </div>
        <p>Ce que voit api.php — pas phpMyAdmin. Si une colonne est trop petite, l'enregistrement du planning échoue avec « Data too long ».${b.mes_plannings && b.mes_plannings.length ? ' Ton planning pèse ' + window.octets(b.mes_plannings[0].octets) + (b.plus_gros_octets ? ' ; le plus gros de la base, ' + window.octets(b.plus_gros_octets) : '') + '.' : ''}</p>
        ${b.php ? html`<p class="gst-mono">PHP ${b.php.version} (${b.php.sapi}) · mémoire ${b.php.memory_limit} · délai ${b.php.max_execution_time} s · envoi max ${b.php.post_max_size} · réponse détachée ${b.php.finish_request ? 'oui' : 'non'}</p>` : ''}
        ${b.erreurs_php ? html`<div class="gst-carte-actions" style="justify-content:space-between;width:100%">
                <span class="gst-carte-mention">Dernières erreurs PHP${b.erreurs_php.length ? ' (' + b.erreurs_php.length + ')' : ''}</span>
                ${b.erreurs_php.length ? html`<button type="button" class="lien-bouton" data-action="gestionBase" data-args="${attr([false, true])}">Vider le journal</button>` : ''}
            </div>
            ${b.erreurs_php.length
                ? html`<pre class="gst-journal">${b.erreurs_php.slice(-15).join('\n')}</pre><p class="gst-carte-note">Une page « 500 Internal Server Error » à l'écran correspond à une de ces lignes : mémoire (« Allowed memory size »), délai (« Maximum execution time ») ou erreur de code.</p>`
                : html`<p class="gst-carte-note">Aucune erreur PHP enregistrée depuis la mise en place du journal.</p>`}` : ''}
        <div class="gst-liste">
            ${(b.colonnes || []).map(function(c) {
                return html`<div class="gst-ligne">
                    <div class="gst-ligne-corps"><div class="gst-ligne-cle">${c.table}.${c.colonne}</div></div>
                    <div class="gst-ligne-actions">
                        <span class="gst-ligne-cle">${c.type || 'absente'}${c.ok ? '' : ' → il faut ' + c.voulu}</span>
                        <span class="puce ${c.ok ? 'puce-ok' : 'puce-danger'}">${c.ok ? 'Bonne taille' : 'Trop petite'}</span>
                    </div>
                </div>`;
            })}
        </div>
        ${pl ? html`<p class="gst-carte-note num"><b>Ton planning « ${pl.id} » : ${brut(window.octets(pl.octets))}</b><br>
            ${pl.taches} tâches (${pl.faites} faites) · ${pl.items} items · ${pl.sous_parties} tâches de sous-parties · ${pl.renforts_pedia} renforts pédiatrie · ${pl.doublons ? pluriel(pl.doublons, 'doublon', 'doublons') : 'aucun doublon'} · ${pl.octets_par_tache} octets par tâche<br>
            Poids par champ : ${Object.keys(pl.par_champ || {}).map(function(k) { return k + ' ' + Math.round(pl.par_champ[k] / 1024) + ' Ko'; }).join(' · ')}<br>
            Par séance : ${Object.keys(pl.par_type || {}).sort().map(function(k) { return k + ' ' + pl.par_type[k]; }).join(' · ')}</p>` : ''}
        ${alerte ? html`<div class="gst-carte-actions">
                <button type="button" class="btn btn-primaire btn-sm" data-action="gestionBase" data-args="${attr([true, false])}"${g.busy ? html` disabled` : ''}${occupe('base:reparer') ? html` aria-busy="true"` : ''}>${ico('wrench', { taille: 16 })}Réparer les colonnes trop petites</button>
            </div>
            <p class="gst-carte-note">Si MySQL refuse (droits insuffisants), le message dira quoi ; il faudra alors passer par phpMyAdmin sur la base ${b.base || ''}.</p>` : ''}
    </div>`;
}

function carteLotsOcr() {
    var g = state.gestion;
    var lots = (g.lots || []).filter(function(l) { return l.statut !== 'APPLIQUÉ' || (Date.now() / 1000 - (l.cree || 0)) < 86400; });
    if (!lots.length) return '';
    return html`<div class="gst-carte">
        <div class="gst-carte-tete">
            <h2>Envois en cours</h2>
            <button type="button" class="lien-bouton" data-action="gestionLotsEtat">Actualiser</button>
        </div>
        <div class="gst-liste">
            ${lots.map(function(l) {
                var fini = l.statut === 'APPLIQUÉ';
                return html`<div class="gst-ligne">
                    <div class="gst-ligne-corps">
                        <div class="gst-ligne-nom">${pluriel(l.documents, 'document', 'documents')} · ${l.genre === 'fiche' ? 'Fiches' : 'Collèges'}</div>
                        <div class="gst-ligne-sous"><span class="gst-ligne-cle">${l.id}</span>${l.pages ? html`<span>${nombre(l.pages)} pages · ${euros(l.cout)} $ (moitié prix)</span>` : ''}</div>
                    </div>
                    <div class="gst-ligne-actions"><span class="puce ${fini ? 'puce-ok' : 'puce-warn puce-attente'}">${l.statut}</span></div>
                </div>`;
            })}
        </div>
    </div>`;
}

function boutonLot(genre, pret) {
    var g = state.gestion;
    return html`<button type="button" class="btn btn-secondaire btn-sm" data-action="gestionLotCreer" data-args="${attr([genre])}"${g.busy || !pret ? html` disabled` : ''}${occupe('lot:' + genre) ? html` aria-busy="true"` : ''}>${ico('stack', { taille: 16 })}Tout passer en lot (moitié prix)</button>`;
}

function ongletDocuments() {
    var g = state.gestion;
    if (g.chargement && !g.etat) {
        return html`<div class="squelettes" aria-hidden="true">
            <div class="squelette squelette-bloc" style="height:96px"></div>
            <div class="squelette squelette-bloc" style="height:180px;margin-top:16px"></div>
        </div><p class="sr-only" role="status">Inventaire en cours…</p>`;
    }
    if (!g.etat) return html`${bandeaux(false)}`;
    // Un inventaire incomplet (réponse tronquée, erreur PHP) ne doit pas laisser la page blanche.
    var e = Object.assign({ colleges: [], fiches: [], items_avec_extraits: 0, pages_payees: 0, cout_total: 0, ocr_modele: '', prix_1000_pages: 4 }, g.etat || {});
    if (!Array.isArray(e.colleges)) e.colleges = [];
    if (!Array.isArray(e.fiches)) e.fiches = [];
    var misesEnForme = e.fiches.filter(function(f) { return f.mise_en_forme; }).length;
    var mesures = [
        [String(e.colleges.length), 'collèges déposés'],
        [String(e.items_avec_extraits), 'items avec extraits'],
        [String(e.fiches.length), 'fiches EDN'],
        [nombre(e.pages_payees) + ' p.', euros(e.cout_total) + ' $ d\'OCR'],
    ];
    return html`
        ${bandeaux(true)}
        ${e.ocr_pret ? '' : html`<div class="gst-bandeau gst-bandeau--warn">
            <span class="puce puce-warn">Clé absente</span>
            <div class="gst-bandeau-corps"><p>Renseigne <code>$mistral_api_key</code> dans <code>config.local.php</code> pour lancer l'OCR.</p></div>
        </div>`}
        <div class="gst-mesures">${mesures.map(function(m) {
            return html`<div class="gst-mesure"><span class="gst-mesure-val">${m[0]}</span><span class="gst-mesure-lib">${m[1]}</span></div>`;
        })}</div>
        ${carteBase()}
        ${carteLotsOcr()}
        <div class="gst-carte">
            <div class="gst-carte-tete">
                <h2>Collèges · extraction du texte</h2>
                <span class="gst-carte-mention">${[e.ocr_modele, (e.prix_1000_pages || 4) + ' $ les 1 000 pages'].filter(Boolean).join(' · ')}</span>
            </div>
            <p>Un collège scanné ne rend aucun texte au lecteur habituel : ses items n'ont alors aucun extrait. En lot, tout part d'un coup à moitié prix et revient sans limite de temps ; à la demande, la lecture se fait par tranches et reprend où elle s'est arrêtée.</p>
            <div class="gst-carte-actions">${boutonLot('college', e.ocr_pret)}</div>
            <div class="gst-liste">
                ${e.colleges.length ? e.colleges.map(function(c) {
                    var enCours = occupe('college:' + c.nom);
                    return html`<div class="gst-ligne">
                        <div class="gst-ligne-corps">
                            <div class="gst-ligne-nom">${c.nom}</div>
                            <div class="gst-ligne-sous"><span>${brut(window.octets(c.octets))}</span>${c.pages_ocr
                                ? html`<span>${c.pages_ocr}${c.total ? ' / ' + c.total : ''} pages lues</span>${jauge(c.pages_ocr, c.total || c.pages_ocr)}`
                                : html`<span>jamais passé à l'OCR</span>`}</div>
                        </div>
                        <div class="gst-ligne-actions">${c.complet
                            ? html`<span class="puce puce-ok">Lu</span>`
                            : html`<button type="button" class="btn btn-secondaire btn-sm" data-action="gestionOcr" data-args="${attr(['college', c.nom])}"${g.busy ? html` disabled` : ''}>${enCours ? html`${g.progres ? html`${g.progres.docs ? g.progres.doc + '/' + g.progres.docs + ' · ' : ''}${g.progres.fait} p.` : 'En cours'}` : (c.pages_ocr ? 'Reprendre' : 'Passer à l\'OCR')}</button>`}
                        </div>
                    </div>`;
                }) : html`<p class="gst-vide">Aucun PDF dans <code>edn_data/college/</code>.</p>`}
            </div>
        </div>
        <div class="gst-carte">
            <div class="gst-carte-tete">
                <h2>Fiches · lecture et mise en forme</h2>
                <span class="gst-carte-mention">${misesEnForme} / ${e.fiches.length} remises en forme</span>
            </div>
            <p>Le texte extrait est débarrassé des en-têtes et pieds de page, redécoupé en sections, puis rendu aux couleurs du site.</p>
            <div class="gst-carte-actions">${boutonLot('fiche', e.ocr_pret)}</div>
            <div class="gst-liste">
                ${e.fiches.length ? e.fiches.slice(0, 400).map(function(f) {
                    return html`<div class="gst-ligne">
                        <span class="puce puce-sans-point num">#${f.item}</span>
                        <div class="gst-ligne-corps"><div class="gst-ligne-nom">${f.nom}</div>
                            ${f.complet ? html`<div class="gst-ligne-sous"><span>${f.pages_ocr} p. lues</span></div>` : ''}</div>
                        <div class="gst-ligne-actions">${f.complet
                            ? (f.mise_en_forme
                                ? html`<button type="button" class="btn btn-fantome btn-sm" data-action="gestionApercu" data-args="${attr([f.item, ''])}" title="Voir la fiche telle qu'elle est mise en forme">${ico('eye', { taille: 16 })}Voir</button>
                                       <button type="button" class="btn btn-fantome btn-sm" data-action="gestionFichePdf" data-args="${attr([f.item])}" title="Ouvrir la version imprimable, puis « Enregistrer en PDF »">${ico('file-pdf', { taille: 16 })}PDF</button>`
                                : html`<button type="button" class="btn btn-fantome btn-sm" data-action="gestionMiseEnForme" data-args="${attr([f.item, ''])}"${g.busy ? html` disabled` : ''}${occupe('forme:' + f.item) ? html` aria-busy="true"` : ''}>Mettre en forme</button>`)
                            : html`<button type="button" class="btn btn-fantome btn-sm" data-action="gestionOcr" data-args="${attr(['fiche', f.item])}"${g.busy ? html` disabled` : ''}${occupe('fiche:' + f.item) ? html` aria-busy="true"` : ''}>OCR</button>`}
                        </div>
                    </div>`;
                }) : html`<p class="gst-vide">Aucune fiche dans <code>edn_data/fiches_edn/</code>.</p>`}
            </div>
        </div>`;
}

// --- onglet Voix ----------------------------------------------------------------------------------------------
function ligneVoix(v) {
    var g = state.gestion;
    var etiquettes = [v.genre || v.gender, (v.langues || []).join(', ')].filter(Boolean).join(' · ');
    return html`<div class="gst-ligne">
        <div class="gst-ligne-corps">
            <div class="gst-ligne-nom">${v.name}${v.fr ? html` <span class="puce puce-sans-point">français</span>` : ''}${v.perso ? html` <span class="puce puce-accent">clonée</span>` : ''}</div>
            <div class="gst-ligne-sous"><span class="gst-ligne-cle">${v.id}</span>${etiquettes ? html`<span>${etiquettes}</span>` : ''}</div>
        </div>
        ${v.perso ? html`<div class="gst-ligne-actions">
            <button type="button" class="ico-btn danger" data-action="gestionVoixSupprimer" data-args="${attr([v.id, v.name || ''])}"${g.busy ? html` disabled` : ''}${occupe('voix:' + v.id) ? html` aria-busy="true"` : ''} aria-label="Supprimer la voix ${v.name || v.id}" title="Supprimer cette voix">${ico('trash-simple', { taille: 18 })}</button>
        </div>` : ''}
    </div>`;
}

function ongletVoix() {
    var g = state.gestion, voix = state.podcastVoiceList || [], b = g.voixBrouillon || {};
    var perso = voix.filter(function(v) { return v.perso; });
    var predef = voix.filter(function(v) { return !v.perso; });
    var dg = g.voixDiag;
    return html`
        ${bandeaux(false)}
        ${state.podcastVoiceAvertissement ? html`<div class="gst-bandeau gst-bandeau--danger" role="alert">
            <span class="puce puce-danger">Voix</span><div class="gst-bandeau-corps"><p>${state.podcastVoiceAvertissement}</p></div></div>` : ''}
        <div class="gst-carte">
            <div class="gst-carte-tete"><h2>Fabriquer une voix</h2></div>
            <p>Dépose un extrait où <b>une seule personne parle</b>, au calme : <b>5 à 25 secondes</b> suffisent (3 au minimum), soit environ 400 Ko en MP3 — <b>1,5 Mo au maximum</b>. L'extrait ne reste pas sur le serveur, il ne fait qu'y transiter. La voix obtenue s'utilise ensuite comme n'importe quelle autre, dans la fenêtre Podcast.</p>
            <div class="gst-grille">
                <div class="champ"><label for="voix-nom">Nom de la voix</label><input id="voix-nom" class="saisie" type="text" value="${b.nom || ''}" placeholder="Ex. Ma voix" maxlength="60" autocomplete="off"></div>
                <div class="champ"><label for="voix-langues">Langues</label><input id="voix-langues" class="saisie" type="text" value="${b.langues || 'fr'}" placeholder="fr" maxlength="40" autocomplete="off"><span class="aide">Codes séparés par une virgule.</span></div>
                <div class="champ"><label for="voix-genre">Genre</label><span class="select-enveloppe"><select id="voix-genre" class="select saisie">
                    ${['', 'female', 'male'].map(function(x) { return html`<option value="${x}"${b.genre === x ? html` selected` : ''}>${x === '' ? 'Non précisé' : (x === 'female' ? 'Féminine' : 'Masculine')}</option>`; })}
                </select></span><span class="aide">Facultatif.</span></div>
                <div class="champ gst-fichier"><label for="voix-fichier">Extrait sonore</label>
                    <input id="voix-fichier" type="file" accept="audio/*,.mp3,.wav,.m4a,.ogg,.opus,.webm,.flac" data-change-action="gestionVoixExtrait">
                    ${b.fichier ? html`<span class="aide num">${b.fichier} · ${brut(window.octets(b.octets || 0))}</span>` : ''}</div>
            </div>
            <div class="gst-carte-actions">
                <button type="button" class="btn btn-primaire" data-action="gestionVoixCloner"${g.busy ? html` disabled` : ''}${occupe('voix') ? html` aria-busy="true"` : ''}>${ico('magic-wand', { taille: 16 })}Créer la voix</button>
            </div>
        </div>
        <div class="gst-carte">
            <div class="gst-carte-tete">
                <h2>${pluriel(voix.length, 'voix disponible', 'voix disponibles')}</h2>
                <button type="button" class="lien-bouton" data-action="gestionVoixActualiser">Actualiser</button>
            </div>
            ${perso.length ? html`<p class="gst-carte-note">Tes voix</p><div class="gst-liste">${perso.map(ligneVoix)}</div>` : ''}
            ${predef.length ? html`<p class="gst-carte-note">Voix du modèle</p><div class="gst-liste">${predef.map(ligneVoix)}</div>` : ''}
            ${voix.length ? '' : html`<p class="gst-vide">Liste des voix en cours de chargement…</p>`}
        </div>
        <div class="gst-carte">
            <div class="gst-carte-tete">
                <h2>Ce que répond l'API</h2>
                <button type="button" class="btn btn-fantome btn-sm" data-action="gestionVoixDiagnostic"${g.busy ? html` disabled` : ''}${occupe('diag') ? html` aria-busy="true"` : ''}>Interroger</button>
            </div>
            <p>À n'utiliser que si la liste des voix reste vide : montre le code de réponse et le début du corps de chaque point d'entrée.</p>
            ${dg ? html`<div class="gst-diag">${(dg.points || []).map(function(pt) {
                return html`<div class="gst-diag-bloc">
                    <div class="gst-ligne-cle">${pt.chemin} → HTTP ${pt.statut}${pt.voix_trouvees ? ' · ' + pt.voix_trouvees + ' voix sous « ' + pt.sous_la_cle + ' »' : ' · aucune voix reconnue'}</div>
                    ${(pt.cles || []).length ? html`<p class="gst-carte-note">clés : ${(pt.cles || []).join(', ')}</p>` : ''}
                    <pre>${pt.corps || ''}</pre>
                </div>`;
            })}</div>` : ''}
        </div>`;
}

// --- onglet Comptes -------------------------------------------------------------------------------------------
function dateFr(v) {
    if (!v) return null;
    var d = new Date(String(v).replace(' ', 'T'));
    return isNaN(d) ? String(v) : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}
function pctReussite(x) { return x == null ? null : Math.round(x * 100) + ' %'; }

function ligneCompte(c) {
    var g = state.gestion, ouvert = g.compteOuvert === c.id, detail = g.compteDetail[c.id];
    var suspendu = c.statut === 'suspendu';
    var L = c.learning || {};
    return html`<li class="cmp${suspendu ? ' cmp--suspendu' : ''}">
        <div class="cmp-tete">
            <button type="button" class="cmp-qui" data-action="compteOuvrir" data-args="${attr([c.id])}" aria-expanded="${ouvert ? 'true' : 'false'}">
                ${ico(ouvert ? 'caret-down' : 'caret-right', { taille: 16 })}
                <span class="cmp-email">${c.email}</span>
            </button>
            <div class="cmp-puces">
                ${c.role === 'admin' ? html`<span class="puce puce-accent">administrateur</span>` : ''}
                ${c.moi ? html`<span class="puce puce-sans-point">toi</span>` : ''}
                <span class="puce ${suspendu ? 'puce-danger' : 'puce-ok'}">${suspendu ? 'accès révoqué' : 'actif'}</span>
                ${c.abo_statut ? html`<span class="puce puce-sans-point">abonnement ${c.abo_statut}${c.abo_plan ? ' · ' + c.abo_plan : ''}</span>` : ''}
            </div>
        </div>
        <dl class="cmp-faits">
            <div><dt>Inscription</dt><dd class="num">${dateFr(c.inscription) || 'inconnue'}${c.inscription && !c.inscription_exacte
                ? html` <span class="cmp-approx" title="La date d'inscription n'était pas enregistrée avant la mise à jour : celle-ci est déduite de la plus ancienne trace laissée par le compte.">≈ estimée</span>` : ''}</dd></div>
            <div><dt>Dernière visite</dt><dd class="num">${dateFr(c.derniere_connexion) || 'jamais depuis la mise à jour'}</dd></div>
            <div><dt>Dernière IP</dt><dd class="num">${c.derniere_ip || '—'}</dd></div>
            <div><dt>Plannings</dt><dd class="num">${c.n_plannings || 0}</dd></div>
            <div><dt>Réponses</dt><dd class="num">${nombre(L.reponses || 0)}${L.reussite != null ? ' · ' + pctReussite(L.reussite) + ' de réussite' : ''}</dd></div>
            <div><dt>Flashcards</dt><dd class="num">${nombre(L.cartes || 0)}</dd></div>
            <div><dt>IA ce mois</dt><dd class="num">${euros((c.cout_mois || 0) / 100)} € · ${pluriel(c.appels_mois || 0, 'appel', 'appels')}</dd></div>
        </dl>
        ${ouvert ? html`<div class="cmp-detail">
            ${!detail ? html`<p class="cmp-vide">Lecture des plannings…</p>`
                : (!detail.length ? html`<p class="cmp-vide">Aucun planning enregistré pour ce compte.</p>`
                : html`<table class="mtable cmp-plannings">
                    <caption class="sr-only">Plannings de ${c.email}</caption>
                    <thead><tr><th scope="col">Planning</th><th scope="col">Séances</th><th scope="col">Faites</th><th scope="col">Période</th><th scope="col">Créé le</th></tr></thead>
                    <tbody>${detail.map(function(p) {
                        return html`<tr>
                            <td>${p.name || p.id}</td>
                            <td class="num">${nombre(p.seances || 0)}</td>
                            <td class="num">${nombre(p.faites || 0)}${p.seances ? ' (' + Math.round(100 * (p.faites || 0) / p.seances) + ' %)' : ''}</td>
                            <td class="num">${p.debut ? dateFr(p.debut) + ' → ' + dateFr(p.fin) : '—'}</td>
                            <td class="num">${dateFr(p.created_at) || '—'}</td>
                        </tr>`;
                    })}</tbody></table>`)}
        </div>` : ''}
        <div class="cmp-actions">
            <button type="button" class="btn btn-secondaire btn-sm" data-action="compteLienReset" data-args="${attr([c.id])}"${occupe('reset-' + c.id) ? html` disabled` : ''}
                title="Fabrique un lien à usage unique, valable une heure, que tu lui transmets. Le mot de passe lui-même n'est pas lisible : il est haché.">${ico('key', { taille: 16 })}Lien de réinitialisation</button>
            ${c.moi ? html`<span class="cmp-note">Tu ne peux pas révoquer ton propre accès.</span>`
                : html`<button type="button" class="btn ${suspendu ? 'btn-secondaire' : 'btn-fantome'} btn-sm" data-action="compteStatut" data-args="${attr([c.id, suspendu ? 'actif' : 'suspendu'])}"${occupe('statut-' + c.id) ? html` disabled` : ''}>
                    ${ico(suspendu ? 'check' : 'prohibit', { taille: 16 })}${suspendu ? 'Rendre l\'accès' : 'Révoquer l\'accès'}</button>`}
        </div>
    </li>`;
}

function ongletComptes() {
    var g = state.gestion, comptes = g.comptes;
    if (occupe('comptes') && !comptes) return html`<div class="squelettes"><div class="squelette squelette-bloc"></div><div class="squelette squelette-bloc"></div></div>`;
    if (!comptes || !comptes.length) return html`<div class="vide">${ico('user')}<h2>Aucun compte</h2><p>Personne ne s'est encore inscrit, ou la base n'a pas répondu.</p>
        <button type="button" class="btn btn-secondaire" data-action="comptesCharger">Réessayer</button></div>`;
    var actifs = comptes.filter(function(c) { return c.statut !== 'suspendu'; }).length;
    return html`<div class="cmp-bloc">
        <p class="cmp-resume num">${pluriel(comptes.length, 'compte', 'comptes')} · ${actifs} actif${actifs > 1 ? 's' : ''}${comptes.length - actifs ? ' · ' + (comptes.length - actifs) + ' révoqué' + (comptes.length - actifs > 1 ? 's' : '') : ''}</p>
        ${g.lienReset ? html`<div class="cmp-lien" role="status">
            <p><strong>Lien de réinitialisation pour ${g.lienReset.email}</strong> — à usage unique, valable ${pluriel(g.lienReset.minutes, 'minute', 'minutes')}. Transmets-le par un moyen que tu juges sûr&nbsp;: quiconque l'ouvre peut choisir un nouveau mot de passe pour ce compte.</p>
            <code class="cmp-lien-url">${g.lienReset.lien}</code>
            <div class="cmp-lien-actions">
                <button type="button" class="btn btn-primaire btn-sm" data-action="compteCopierLien">${ico('copy', { taille: 16 })}Copier</button>
                <button type="button" class="btn btn-fantome btn-sm" data-action="compteFermerLien">Fermer</button>
            </div>
        </div>` : ''}
        <ul class="cmp-liste">${comptes.map(ligneCompte)}</ul>
    </div>`;
}

// --- onglet Couverture ----------------------------------------------------------------------------------------
function emplacementCouverture(livre, titre, phrase) {
    var g = state.gestion, c = (g.couvs || {})[livre];
    return html`<div class="gst-couv">
        <div class="gst-couv-titre">${titre}</div>
        ${c && c.apercu
            ? html`<img class="gst-couv-apercu" src="${c.apercu}" alt="Couverture actuelle du ${titre.toLowerCase()}">`
            : html`<div class="gst-couv-vide">Aucune image : couverture dessinée</div>`}
        <p class="gst-carte-note">${c ? html`${c.fichier} · ${brut(window.octets(c.octets))}${c.commune ? ' · couverture commune' : ''}` : phrase}</p>
        <div class="champ gst-fichier">
            <label for="livre-couverture-${livre}">${c && !c.commune ? 'Remplacer l\'image' : 'Choisir une image'}</label>
            <input type="file" id="livre-couverture-${livre}" accept="image/jpeg,image/png,image/webp" data-change-action="gestionCouvertureChoisir" data-args="${attr([livre])}"${g.busy ? html` disabled` : ''}>
        </div>
        ${c && !c.commune ? html`<div class="gst-carte-actions">
            <button type="button" class="btn btn-fantome btn-sm" data-action="gestionCouvertureRetirer" data-args="${attr([livre])}"${g.busy ? html` disabled` : ''}${occupe('couverture') ? html` aria-busy="true"` : ''}>Retirer</button>
        </div>` : ''}
    </div>`;
}

function ongletCouverture() {
    actions.gestionCouvertureCharger();
    return html`
        ${bandeaux(false)}
        <div class="gst-carte">
            <div class="gst-couvs">
                ${emplacementCouverture('qcm', 'Livre des QCM', 'Le livre des QCM utilise la couverture dessinée par le site.')}
                ${emplacementCouverture('lca', 'Livre de LCA', 'Le livre de LCA utilise la couverture dessinée par le site.')}
            </div>
            <p class="gst-carte-note">JPEG, PNG ou WebP, 12 Mo au plus. Le format d'une affiche (deux tiers de large pour trois de haut) tombe juste sur une page. Les images sont rangées dans <code>edn_data/livre/</code> et incorporées au PDF, qui reste donc complet hors ligne. Une image nommée <code>couverture.*</code> déposée à la main sert aux deux livres tant qu'ils n'ont pas la leur.</p>
        </div>`;
}

// --- la page --------------------------------------------------------------------------------------------------
export function renderGestion() {
    if (!window.estAdmin()) {
        return html`<div class="gst"><div class="vide">
            ${ico('lock-simple', { taille: 32 })}
            <h2>Réservé à l'administrateur</h2>
            <p>Cette page pilote les documents du serveur.</p>
        </div></div>`;
    }
    var g = state.gestion;
    if (!g.etat && !g.chargement) actions.gestionCharger();
    var onglet = g.onglet || 'documents';
    var t = TITRES[onglet] || TITRES.documents;
    return html`<div class="gst fade-in">
        <header class="jour-entete">
            <div class="jour-entete-texte">
                <h1 class="jour-date">${t[0]}</h1>
                <p class="gst-resume">${t[1]}</p>
            </div>
        </header>
        <div class="gst-onglets"><div class="segments" role="tablist" aria-label="Sections de la gestion">
            ${ONGLETS.map(function(o) {
                return html`<button type="button" role="tab" aria-selected="${onglet === o[0] ? 'true' : 'false'}" data-action="gestionOnglet" data-args="${attr([o[0]])}">${ico(o[2], { taille: 16 })}${o[1]}</button>`;
            })}
        </div></div>
        ${onglet === 'voix' ? ongletVoix() : (onglet === 'couverture' ? ongletCouverture() : (onglet === 'comptes' ? ongletComptes() : ongletDocuments()))}
    </div>`;
}

// Les rendus historiques portaient les trois onglets par trois fonctions : on les repose toutes pour que rien
// d'autre dans le moteur n'appelle encore l'ancien balisage.
export function renderGestionVoix() { return ongletVoix(); }
export function renderGestionCouverture() { return ongletCouverture(); }

window.renderGestion = renderGestion;
window.renderGestionVoix = renderGestionVoix;
window.renderGestionCouverture = renderGestionCouverture;
